/* eslint-disable sonarjs/cognitive-complexity */
import { ActionIcon, Flex, Tooltip } from '@mantine/core';
import { IconHistory, IconPlayerStop, IconPlayerStopFilled } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import FastModeCheckbox from '@/components/shared/FastModeCheckbox';
import FieldTextarea from '@/components/shared/form/FieldTextarea';
import ChatLoader from '@/components/ui/ChatLoader';
import { IconSend } from '@/components/ui/icons/IconSend';
import { ROUTES } from '@/configs/routes';
import { useOrganizationChatAiPageStore } from '@/stores/local/pages/OrganizationChatAiPageStore';
import { useDevices } from '@/utils/devices';

import ChartTips from '../ChartTips';
import MobileChartTipsDrawer from '../ChartTips/MobileChartTipsDrawer';

import s from './ChartInput.module.scss';

type ChartInputProps = {
  className?: string;
  hideTips?: boolean;
  tipsEmptyText?: string;
};

const ChartInput: React.FC<ChartInputProps> = ({ className, hideTips = false, tipsEmptyText }) => {
  const { isCompactViewport } = useDevices();

  const { pathname } = useLocation();

  const {
    fieldText,
    meta,
    sendMessage,
    cancel,
    messages,
    loaderText,
    canOpenCurrentToolCallHistory,
    toolCallHistory,
    openSessionsHistory,
  } = useOrganizationChatAiPageStore();

  const isEventsView = React.useMemo(
    () => Boolean(matchPath({ path: ROUTES.organization.documents.path, end: true }, pathname)),
    [pathname],
  );
  const hasMessages = messages.length > 0;
  const shouldShowTips = !hideTips && !meta.isLoading && !hasMessages;
  const shouldRenderHeader = shouldShowTips || meta.isLoading;

  const isChatWithoutDocument = React.useMemo(() => {
    const chatMatch = matchPath(ROUTES.organization.documents.chat.path, pathname);

    return Boolean(chatMatch && !chatMatch.params?.documentId);
  }, [pathname]);

  const shouldRenderHeaderContent = shouldRenderHeader;
  const shouldRenderHeaderShell =
    shouldRenderHeaderContent || meta.isLoading || (isChatWithoutDocument && !hasMessages);
  const loaderClassName = cn(s['root__loader-indicator'], isEventsView && s['root__loader-indicator_events']);
  const inputRightSectionWidth = React.useMemo(() => {
    if (isEventsView) {
      return 112;
    }

    if (isCompactViewport) {
      return 56;
    }

    return 64;
  }, [isCompactViewport, isEventsView]);

  const handleLoaderTextClick = React.useCallback(() => {
    if (!canOpenCurrentToolCallHistory || !toolCallHistory.currentMessageId) {
      return;
    }

    toolCallHistory.openForMessage(toolCallHistory.currentMessageId);
  }, [canOpenCurrentToolCallHistory, toolCallHistory]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Enter') {
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        const { selectionStart, selectionEnd, value } = e.currentTarget;

        if (selectionStart === null || selectionEnd === null) {
          fieldText.setValue(`${value}\n`);

          return;
        }
        const nextValue = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`;

        fieldText.setValue(nextValue);

        requestAnimationFrame(() => {
          const caretPosition = selectionStart + 1;

          e.currentTarget.setSelectionRange(caretPosition, caretPosition);
        });

        return;
      }

      if (e.shiftKey) {
        return;
      }

      if (meta.isLoading) {
        return;
      }

      e.preventDefault();
      sendMessage();
    },
    [fieldText, meta.isLoading, sendMessage],
  );

  return (
    <div className={cn(s.root, meta.isLoading && s.root_disabled, meta.isLoading && s.root_loading, className)}>
      {meta.isLoading &&
        (isCompactViewport ? (
          <ChatLoader
            className={loaderClassName}
            text={loaderText}
            onTextClick={canOpenCurrentToolCallHistory ? handleLoaderTextClick : undefined}
          />
        ) : (
          <ChatLoader
            variant="desktop"
            className={loaderClassName}
            text={loaderText}
            onTextClick={canOpenCurrentToolCallHistory ? handleLoaderTextClick : undefined}
          />
        ))}
      {shouldRenderHeaderShell && (
        <div className={s.root__header}>
          {shouldRenderHeaderContent && shouldShowTips && (
            <>
              <div className={s['root__tips-desktop']}>
                <ChartTips emptyText={tipsEmptyText} />
              </div>
              <div className={s['root__tips-mobile']}>
                <MobileChartTipsDrawer />
              </div>
            </>
          )}
        </div>
      )}
      <FastModeCheckbox className={s['root__fast-mode']} />
      <FieldTextarea
        onKeyDown={handleKeyDown}
        autosize
        autoFocus={!isCompactViewport}
        placeholder="Напишите свой вопрос"
        className={s.root__field}
        classNames={{ input: s.root__input, section: s.root__section, wrapper: s.root__wrapper }}
        maxRows={6}
        rightSectionWidth={inputRightSectionWidth}
        rightSectionPointerEvents="auto"
        rightSection={
          <Flex className={s.root__actions} justify="center" align="center" gap="md">
            {meta.isLoading ? (
              <ActionIcon
                className={s.root__action}
                color={isCompactViewport ? 'blue' : 'gray'}
                variant={isCompactViewport ? 'transparent' : 'filled'}
                onClick={cancel}
              >
                {isCompactViewport ? <IconPlayerStop size={20} /> : <IconPlayerStopFilled size={14} />}
              </ActionIcon>
            ) : (
              <ActionIcon
                onClick={sendMessage}
                className={s['root__action-icon']}
                variant="transparent"
                size={isCompactViewport ? 'md' : 'xl'}
              >
                <IconSend className={s.root__icon} />
              </ActionIcon>
            )}
            {isEventsView && (
              <Tooltip label="История общения">
                <ActionIcon
                  className={s['root__action-icon']}
                  variant={isCompactViewport ? 'transparent' : 'light'}
                  size={isCompactViewport ? 'md' : 'lg'}
                  onClick={openSessionsHistory}
                >
                  <IconHistory size={isCompactViewport ? 40 : 20} />
                </ActionIcon>
              </Tooltip>
            )}
          </Flex>
        }
        store={fieldText}
      />
    </div>
  );
};

export default observer(ChartInput);
